import { toBase64Url, utf8 } from "./util";

/**
 * Token + key derivation.
 *
 * The `token` is 32 bytes of CSPRNG output — a high-entropy shared secret that
 * lives only in the link's `#fragment` and never reaches the server. From it we
 * derive, via domain-separated HKDF-SHA256:
 *   - roomId : a PUBLIC rendezvous label (safe for the signaling server to see)
 *   - psk    : the SECRET pre-shared key that authenticates the handshake
 *
 * HKDF is one-way and the two labels are independent, so the server seeing
 * `roomId` reveals nothing about `psk` or the token.
 */

export const TOKEN_BYTES = 32;

// Public, constant application salt. Domain separation is done via `info`.
const HKDF_SALT = utf8("enclave/salt/v1");

export function generateToken(): Uint8Array {
  const token = new Uint8Array(TOKEN_BYTES);
  crypto.getRandomValues(token);
  return token;
}

/** HKDF-SHA256 (extract+expand) via the native Web Crypto API. */
export async function hkdf(
  ikm: Uint8Array,
  salt: Uint8Array,
  info: string,
  length: number
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", ikm, "HKDF", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt, info: utf8(info) },
    key,
    length * 8
  );
  return new Uint8Array(bits);
}

export async function deriveRoomId(token: Uint8Array): Promise<string> {
  const raw = await hkdf(token, HKDF_SALT, "enclave/room/v1", 16);
  return toBase64Url(raw);
}

export async function derivePsk(token: Uint8Array): Promise<Uint8Array> {
  return hkdf(token, HKDF_SALT, "enclave/psk/v1", 32);
}
